/**
 * Design System Tab Component
 *
 * Wraps MUI Tab with design system token integration.
 * Used with DSTabs component for consistent tab styling.
 *
 * Features:
 * - Design system color scheme
 * - Icon and label support with proper spacing
 * - Proper accessibility attributes
 * - Custom sx prop support
 * - Responsive behavior
 *
 * Usage:
 * <DSTabs value={activeTab} onChange={handleTabChange}>
 *   <DSTab label="Overview" id="tab-0" aria-controls="tabpanel-0" />
 *   <DSTab label="Details" id="tab-1" aria-controls="tabpanel-1" />
 *   <DSTab icon={<InfoIcon />} label="Info" id="tab-2" />
 * </DSTabs>
 */

import React from 'react';
import { Tab } from '@mui/material';
import PropTypes from 'prop-types';

/**
 * DSTab component - Design System Tab wrapper
 *
 * @component
 * @param {string} label - Tab label text
 * @param {React.ReactNode} icon - Optional icon to display
 * @param {object} sx - Additional MUI sx prop for custom styling
 * @param {boolean} disabled - Disable the tab
 * @param {string} id - HTML id attribute
 * @param {...rest} rest - Any other MUI Tab props
 *
 * @returns {React.ReactElement} Tab component with design system styling
 */
const DSTab = React.forwardRef(
  (
    {
      label,
      icon = null,
      sx = {},
      disabled = false,
      id,
      ...rest
    },
    ref
  ) => {
    // Design system tab styling
    const finalSx = {
      // Base tab styling
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '14px',
      color: 'var(--neutral-600)',
      transition: 'color var(--duration-fast) var(--ease-out), background-color var(--duration-fast) var(--ease-out)',
      minWidth: '120px',
      padding: '12px 16px',

      // Hover state
      '&:hover': {
        color: 'var(--teal-600)',
        backgroundColor: 'var(--teal-50)',
      },

      // Active/selected state
      '&.Mui-selected': {
        color: 'var(--teal-600)',
        fontWeight: 700,
      },

      // Disabled state
      '&.Mui-disabled': {
        color: 'var(--neutral-400)',
        opacity: 0.6,
      },

      // Icon styling
      ...(icon && {
        '& .MuiTab-iconWrapper': {
          marginRight: 'var(--space-2)',
          marginBottom: 0,
          fontSize: '24px',
        },
      }),

      // Responsive adjustments
      '@media (max-width: 576px)': {
        minWidth: 'auto',
        flex: 1,
        fontSize: '13px',
        padding: '8px 12px',
      },

      // Merge custom sx (custom overrides take precedence)
      ...sx,
    };

    // Construct label with icon if provided
    const tabLabel = icon ? (
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
        }}
      >
        {icon}
        {label}
      </span>
    ) : (
      label
    );

    return (
      <Tab
        ref={ref}
        label={tabLabel}
        disabled={disabled}
        id={id}
        sx={finalSx}
        {...rest}
      />
    );
  }
);

// Display name for debugging
DSTab.displayName = 'DSTab';

// PropTypes for type checking
DSTab.propTypes = {
  /**
   * The label text to display on the tab
   */
  label: PropTypes.node.isRequired,

  /**
   * Optional icon component to display before the label
   */
  icon: PropTypes.node,

  /**
   * Additional MUI sx prop for custom styling
   */
  sx: PropTypes.object,

  /**
   * If true, the tab will be disabled
   */
  disabled: PropTypes.bool,

  /**
   * HTML id attribute for accessibility
   */
  id: PropTypes.string,

  /**
   * aria-controls attribute linking to the associated panel
   */
  'aria-controls': PropTypes.string,

  /**
   * Class name for styling
   */
  className: PropTypes.string,

  /**
   * Icon position (top, bottom, start, end)
   */
  iconPosition: PropTypes.oneOf(['top', 'bottom', 'start', 'end']),

  /**
   * Wrapper class name
   */
  wrapped: PropTypes.bool,
};


export default DSTab;
